<template>
  <v-container grid-list-md class="occupancy-statistics">
    <v-card :class="[{'low-profile': hotel !== undefined}, {'padding-profile': hotel === undefined}]">
      <v-card-text style="padding: 0">
        <apexcharts ref="occupancyChart"
                   type="area" height="250"
                   :options="statistics.occupancy.options"
                   :series="getSeries()">
        </apexcharts>
        <v-layout wrap>
          <v-flex xs12 sm12 md6 class="text-left">
            <v-btn outlined @click="statistics.occupancy.seriesLength = 'all'"
                   style="margin-top: 5px"
                   :disabled="statistics.occupancy.seriesMode === 'final'"
                   class="toggle-series-button">
              <v-icon v-if="statistics.occupancy.seriesLength === 'all'" color="green">done</v-icon>
              <v-icon v-else>close</v-icon>
              Alle Monate
            </v-btn>
            <v-btn outlined @click="statistics.occupancy.seriesLength = 'one'"
                   style="margin-top: 5px"
                   :disabled="statistics.occupancy.seriesMode === 'final'"
                   class="toggle-series-button">
              <v-icon v-if="statistics.occupancy.seriesLength === 'one'" color="green">done</v-icon>
              <v-icon v-else>close</v-icon>
              1 Monat
            </v-btn>
            <v-btn outlined @click="statistics.occupancy.seriesLength = 'three'"
                   :disabled="statistics.occupancy.seriesMode === 'final'"
                   style="margin-top: 5px"
                   class="toggle-series-button">
              <v-icon v-if="statistics.occupancy.seriesLength === 'three'" color="green">done</v-icon>
              <v-icon v-else>close</v-icon>
              3 Monate
            </v-btn>
            <v-btn outlined @click="statistics.occupancy.seriesLength = 'six'"
                   :disabled="statistics.occupancy.seriesMode === 'final'"
                   style="margin-top: 5px"
                   class="toggle-series-button">
              <v-icon v-if="statistics.occupancy.seriesLength === 'six'" color="green">done</v-icon>
              <v-icon v-else>close</v-icon>
              6 Monate
            </v-btn>
          </v-flex>
          <v-flex xs12 sm12 md6 class="text-right">
            <v-btn outlined @click="toggleOccupancySeries()"
                   style="margin-top: 5px"
                   class="toggle-series-button"
                   v-if="$auth.check('ROLE_ADMIN') || $auth.check('ROLE_MODERATOR')">
              <span v-if="statistics.occupancy.seriesMode === 'final'">Prognose</span>
              <span v-else>Hotelauslastung</span>
            </v-btn>
            <v-btn outlined @click="toggleOccupancyChart()"
                   style="margin-top: 5px"
                   class="toggle-charts-button">
              <v-icon v-if="statistics.occupancy.chartMode === 'area'">bar_chart</v-icon>
              <v-icon color="green" v-else>bar_chart</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
  const Apexcharts = () => import(/* webpackChunkName: "apexcharts" */ 'vue-apexcharts');

  export default {
    name: 'OccupancyStatistics',
    components: {
      Apexcharts
    },
    props: {
      hotel: undefined
    },
    data() {
      return {
        initialized: false,
        id: undefined,
        chartMode: 'area',
        statistics: {
          occupancy: {
            summary: undefined,
            seriesLength: 'all',
            chartMode: 'area',
            seriesMode: 'forecast',
            options: {
              dataLabels: {
                enabled: false
              },
              legend: {
                show: true,
                position: 'top',
                horizontalAlign: 'left',
                showForSingleSeries: true,
                fontFamily: 'Open Sans, sans-serif',
                fontSize: '18px',
                markers: {
                  width: 0,
                  height: 0
                },
                onItemClick: {
                  toggleDataSeries: false
                }
              },
              stroke: {
                width: 1,
                curve: 'straight'
              },
              yaxis: {
                min: 0,
                max: 100
              },
              xaxis: {
                type: 'datetime',
                min: undefined,
                labels: {
                  datetimeFormatter: {
                    year: 'dd MMM yyyy',
                    month: "dd MMM yyyy",
                    day: 'dd MMM yyyy',
                    hour: 'HH:mm'
                  },
                  style: {
                    colors: [],
                    fontSize: '10px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    cssClass: 'apexcharts-xaxis-label'
                  },
                },
                axisBorder: {
                  color: '#484e4e'
                },
                tooltip: {
                  enabled: true
                }
              },
              colors: ['#1dd357'],
              fill: {
                type: 'gradient',
                gradient: {
                  shadeIntensity: 1,
                  opacityFrom: 0.3,
                  opacityTo: 0,
                  stops: [0, 100]
                }
              },
              tooltip: {
                enabled: true,
                x: {
                  show: false,
                  format: 'ddd. dd MMMM yyyy',
                  formatter: undefined
                },
                y: {
                  formatter: (value) => {
                    if (value === -1) {
                      return 'Betriebsurlaub';
                    } else {
                      const label = this.statistics.occupancy.seriesMode === 'forecast'
                        ? 'Prognose: ' : 'Auslastung: ';
                      return label + value + '%';
                    }
                  },
                  title: {
                    formatter: () => {
                      return '';
                    }
                  }
                },
                marker: {
                  show: false
                },
              },
              chart: {
                animations: {
                  enabled: false
                },
                dropShadow: {
                  enabled: () => this.statistics.occupancy.chartMode === 'area',
                  top: 0,
                  left: 0,
                  blur: 0.5,
                  opacity: 0.3
                },
                defaultLocale: 'de',
                locales: [{
                  name: 'de',
                  options: {
                    months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
                    shortMonths: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
                    days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
                    shortDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
                    toolbar: {
                      download: 'SVG herunterladen',
                      selection: 'Auswahl',
                      selectionZoom: 'Auswahl Zoom',
                      zoomIn: 'Zoom In',
                      zoomOut: 'Zoom Out',
                      pan: 'Panning',
                      reset: 'Reset Zoom'
                    }
                  }
                }],
                toolbar: {
                  show: true,
                  tools: {
                    download: true,
                    selection: false,
                    zoom: false,
                    zoomin: false,
                    zoomout: false,
                    pan: false,
                    reset: false
                  }
                }
              }
            },
            series: [],
            forecast: []
          }
        },
      };
    },
    mounted() {
      if (this.$auth.check('ROLE_ADMIN') || this.$auth.check('ROLE_MODERATOR')) {
        if (!this.initialized) {
          this.id = this.hotel.id;
          this.fetchOccupancySummary();
        }
        this.globalEvents.$on('hotelSelected', (hotel) => {
          this.id = hotel.id;
          this.fetchOccupancySummary();
        });
      } else {
        this.fetchOccupancySummary();
      }
    },
    methods: {
      fetchOccupancySummary() {
        this.statistics.occupancy.series = [];
        this.statistics.occupancy.forecast = [];
        if (this.hotel !== undefined) {
          this.$http.get('statistic/occupancy/'.concat(this.id))
            .then((data) => {
              const statistics = {
                summary: data.body
              };
              if (statistics.summary.occupancyMonthStatistics !== undefined
                && statistics.summary.occupancyMonthStatistics.length > 0) {
                this.prepareOccupancyFinal(statistics);
              }
              if (statistics.summary.dynamicDays !== undefined
                && statistics.summary.dynamicDays.length > 0) {
                this.prepareOccupancyForecast(statistics);
              }
            });
        } else {
          const statistics = this.$store.getters.getOccupancySummary;
          if (statistics.summary === undefined || statistics.updated === undefined
            || this.$moment(statistics.updated).isBefore(this.$moment().endOf('day'))) {
            this.$http.get('statistic/occupancy').then((data) => {
              const statistic = {
                occupancy: {
                  summary: data.body
                }
              };
              this.$store.dispatch('setOccupancySummary', { statistic: statistic });
              this.prepareOccupancySummary();
            });
          } else {
            this.prepareOccupancySummary();
          }
        }
        this.initialized = true;
      },
      prepareOccupancySummary() {
        const statistics = this.$store.getters.getOccupancySummary;
        if (statistics.summary.occupancyMonthStatistics !== undefined
          && statistics.summary.occupancyMonthStatistics.length > 0) {
          this.prepareOccupancyFinal(statistics);
        }
        if (statistics.summary.dynamicDays !== undefined
                && statistics.summary.dynamicDays.length > 0) {
          this.prepareOccupancyForecast(statistics);
        }
      },
      prepareOccupancyFinal(statistics) {
        const firstDate = this.$moment(statistics.summary.
          occupancyMonthStatistics[0].firstDate);
        this.statistics.occupancy.options.xaxis.min = firstDate.valueOf();
        const series = {
          name: 'Hotelauslastung (für die letzten 100 Tage)',
          data: []
        };
        for (let i = 0; i < statistics.summary.occupancyMonthStatistics.length; i += 1) {
          const month = statistics.summary.occupancyMonthStatistics[i];
          let date = this.$moment(month.firstDate);
          if (this.checkOccupancyPeriod(date)) {
            const days = month.occupancy.split(';');
            for (let j = 0; j < days.length; j += 1) {
              const day = days[j];
              series.data.push([date.valueOf(), day]);
              date = date.add(1, 'days');
            }
          }
        }
        this.statistics.occupancy.series.push(series);
      },
      prepareOccupancyForecast(statistics) {
        const forecast = {
          name: 'Prognose für die Hotelauslastung',
          data: []
        };
        for (let i = 0; i < statistics.summary.dynamicDays.length; i += 1) {
          const day = statistics.summary.dynamicDays[i];
          forecast.data.push([this.$moment(day.from).valueOf(), this.checkHoliday(day, statistics.summary.holidays)]);
        }
        this.statistics.occupancy.forecast.push(forecast);
      },
      checkHoliday(day, holidays) {
        const date = this.$moment(day.from).endOf('day');
        for (let i = 0; i < holidays.length; i += 1) {
          const holiday = holidays[i];
          const from = this.$moment(holiday.from).endOf('day');
          const to = this.$moment(holiday.to).endOf('day');
          if (from.isSameOrBefore(date) && to.isSameOrAfter(date)) {
            return -1;
          }
        }
        return day.occupancyRate;
      },
      toggleOccupancyChart() {
        if (this.statistics.occupancy.chartMode === 'area') {
          this.$refs.occupancyChart.updateOptions({
            chart: {type: 'bar'},
            fill: {
              type: 'solid',
              opacity: 1
            },
            colors: [function({value}) {
              if (value < 30) {return '#e10000';}
              else if (value < 70) {return '#ffbd47';}
              else {return '#1dd357';}
            }]
          });
          this.statistics.occupancy.chartMode = 'bar';
        } else {
          this.$refs.occupancyChart.updateOptions({
            chart: {type: 'area'},
            colors: ['#1dd357'],
            fill: {
              type: 'gradient',
              gradient: {
                shadeIntensity: 0,
                opacityFrom: 0.3,
                opacityTo: 0,
                stops: [0, 100]
              }
            }
          });
          this.statistics.occupancy.chartMode = 'area';
        }
      },
      checkOccupancyPeriod(date) {
        const minimum = this.$moment().subtract(100, 'days');
        return minimum.isBefore(date);
      },
      getSeries() {
        const series = [{
          name: 'Prognose für die Hotelauslastung',
          data: []
        }];
        if (this.statistics.occupancy.seriesMode === 'final') {
          return this.statistics.occupancy.series;
        }
        if (this.statistics.occupancy.seriesLength === 'all') {
          return this.statistics.occupancy.forecast;
        }
        const forecast = this.statistics.occupancy.forecast[0].data;
        let end = this.$moment(forecast[0][0]).add(1, 'month').endOf('day');
        if (this.statistics.occupancy.seriesLength === 'three') {
          end = this.$moment(forecast[0][0]).add(3, 'month').endOf('day');
        } else if (this.statistics.occupancy.seriesLength === 'six') {
          end = this.$moment(forecast[0][0]).add(6, 'month').endOf('day');
        }
        for (let i = 0; i < forecast.length; i += 1) {
          const day = this.$moment(forecast[i][0]).endOf('day');
          if (day.isSameOrBefore(end)) {
            series[0].data.push(forecast[i]);
          } else {
            break;
          }
        }
        return series;
      },
      toggleOccupancySeries() {
        if (this.statistics.occupancy.seriesMode === 'final') {
          this.statistics.occupancy.seriesMode = 'forecast';
        } else {
          this.statistics.occupancy.seriesMode = 'final';
          this.statistics.occupancy.seriesLength = 'all'
        }
      }
    }
  };
</script>

<style lang="scss">
  @import "../../assets/scss/variables";
  .occupancy-statistics {
    padding: 0;

    .apexcharts-menu-icon {
      color: $primary-text;
      svg {
        fill: $primary-text;
      }
      &:hover {
        color: $primary;
        svg {
          fill: $primary;
        }
      }
    }
    .apexcharts-menu-item.exportSVG {
      display: none;
    }
    .apexcharts-xaxistooltip, .apexcharts-tooltip {
      background: url('../../assets/images/crossword.png');
      .apexcharts-xaxistooltip-text {
        font-weight: 600;
      }
    }
    .toggle-charts-button {
      margin-top: 0;
      margin-left: 10px;
      margin-bottom: 0;
      height: 25px;
      min-width: 20px;
      padding: 0;
      &:hover {
        color: $primary;
      }
    }
    .toggle-series-button {
      margin-top: 0;
      margin-right: 10px;
      margin-bottom: 0;
      height: 25px;
      min-width: 20px;
      padding: 0 5px;
      &:hover {
        color: $primary;
      }
      .v-btn__content {
        font-size: 12px;
        .v-icon {
          font-size: 12px;
          margin-right: 5px;
        }
      }
      .v-btn__loading {
        .v-progress-circular {
          height: 15px !important;
          width: 15px !important;
        }
      }
    }
    .low-profile {
      box-shadow: none;
    }
    .padding-profile {
      padding: 10px;
    }
  }
</style>
